import { themeColors } from '../../themeColors';
/**
 * @file   src\components\Introduction\PropertyModal.js
 * @brief  This file is responsible for the user to choose the property for sending the introduction.
 * @date   June, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useState } from "react";
import Modal from "react-bootstrap/Modal";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { pageURLs } from "@/constants";
import noImage from "../../images/no-image-placeholder.png";
import PremiumPlanModal from "../../components/PlanUpgrade/PremiumPlanModal";
import BasicPlanModal from "../../components/PlanUpgrade/BasicPlanModal";
import { getUserType } from "../../helpers/common";
import { USER_TYPE_AGENT } from "../../constants/common";
import { getInventoryById } from "../../services/inventoryService";
import usePremiumUser from "../../hooks/usePremium";

const PropertyModal = (props) => {
  // Navigate object creation.
  const navigate = useNavigate();

  // Initialize component state variables and constants.
  const propertyList = props.propertyList;
  const selectedPropertyId = props.selectedPropertyId;
  const { userCount, allowedCount, userDetails, updateReloadParam } = props;
  const wantAdId = props.wantAdId;
  const userType = getUserType();
  const { shouldToggleColor } = usePremiumUser();
  const [showPremiumPlanModal, setPremiumPlanModal] = useState(false);
  const PremiumPlanModalClose = () => setPremiumPlanModal(false);
  const PremiumPlanModalShow = () => setPremiumPlanModal(true);
  const [showBasicPlanModal, setBasicPlanModal] = useState(false);
  const BasicPlanModalClose = () => setBasicPlanModal(false);
  const BasicPlanModalShow = () => setBasicPlanModal(true);
  const [searchQuery, setSearchQuery] = useState("");

  // To handle the property selection radio button
  const handleRadioButtonChange = (property) => {
    if (property?.photo) {
      props.handlePropertyChange(property);
      props.onHide();
    } else {
      getInventoryDetails(property?.Inv_Id);
    }
  };

  // To get the inventory details
  const getInventoryDetails = async (inventoryId) => {
    if (inventoryId) {
      await getInventoryById(inventoryId)
        .then((response) => {
          const responseResult = _.get(response, "result", null);
          if (response.errorCode === 0 && responseResult) {
            if (userType === USER_TYPE_AGENT) {
              navigate(pageURLs.agentCreateInventory, {
                state: {
                  data: responseResult,
                  inventoryId: inventoryId,
                  isFromIntroduction: true,
                  wantAdId: props.wantAdId,
                },
              });
            } else {
              navigate(pageURLs.memberEditInventory, {
                state: {
                  data: responseResult,
                  isFromIntroduction: true,
                  wantAdId: props.wantAdId,
                },
              });
            }
          } else {
            console.log("Something went wrong");
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
    } else {
      userDetails?.userType == 1
        ? navigate(pageURLs.memberCreateInventory)
        : navigate(pageURLs.agentCreateInventory);
    }
  };

  // To handle add new property action
  const addNewpropertyNavigation = () => {
    if (userCount?.Inventory >= allowedCount?.Inventory) {
      if (userType === USER_TYPE_AGENT) {
        PremiumPlanModalShow();
      } else {
        BasicPlanModalShow();
      }
    } else {
      userType === USER_TYPE_AGENT
        ? navigate(pageURLs.agentCreateInventory, {
            state: { isFromIntroduction: true, wantAdId: wantAdId },
          })
        : navigate(pageURLs.memberCreateInventory);
    }
  };

  return (
    <>
      <Modal
        show
        centered
        {...props}
        size="md"
        aria-labelledby="contained-modal-title-vcenter"
        className="modal-right-aligned"
        dialogClassName={shouldToggleColor ? "property-modal-bls" : ""}
      >
        <Modal.Body className="">
          <div className=" position-relative">
            <div className="sub-heading-small position-relative mb-4">
              <a
                role="button"
                className="sub-heading-backbtn"
                href={void 0}
                onClick={props.onHide}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M10.1003 5.39966C10.3932 5.69256 10.3932 6.16743 10.1003 6.46032L5.31066 11.25H20.5C20.9142 11.25 21.25 11.5858 21.25 12C21.25 12.4142 20.9142 12.75 20.5 12.75H5.31066L10.1003 17.5397C10.3932 17.8326 10.3932 18.3074 10.1003 18.6003C9.80744 18.8932 9.33256 18.8932 9.03967 18.6003L2.96967 12.5303C2.82902 12.3897 2.75 12.1989 2.75 12C2.75 11.8011 2.82902 11.6103 2.96967 11.4697L9.03967 5.39966C9.33256 5.10677 9.80744 5.10677 10.1003 5.39966Z"
                    fill={themeColors.neutral890}
                  />
                </svg>
              </a>
              Choose property
            </div>
            {/* Search bar for BLS agents with multiple properties */}
            {shouldToggleColor && propertyList && propertyList.length > 3 && (
              <div style={{ marginBottom: 12 }}>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by address..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ fontSize: 13, padding: '6px 12px', borderRadius: 8 }}
                />
              </div>
            )}
            <div
              className="modal-choose-property modal-v-scroll"
              style={shouldToggleColor ? { marginBottom: 0, maxHeight: '60vh' } : {}}
            >
              {propertyList &&
                propertyList
                .filter((property) => {
                  if (!searchQuery) return true;
                  const q = searchQuery.toLowerCase();
                  return (property?.Location || "").toLowerCase().includes(q) ||
                    (property?.City || "").toLowerCase().includes(q) ||
                    (property?.ZIP || "").includes(q);
                })
                .map((property) => {
                  // For non-BLS agents, hide private inventory entirely (original behavior)
                  if (!shouldToggleColor && userType === USER_TYPE_AGENT && property?.Represent_as_Seller) return null;

                  // Prospect inventory (Represent_as_Seller=1) greyed out for BLS
                  // Active listings (Represent_as_Seller=0/falsy) are sendable
                  const isPrivateInventory = shouldToggleColor && property?.Represent_as_Seller == 1;

                  let intoSentClassName = property?.IntroSentDate
                    ? "intro-sent"
                    : "";
                  let intoDeclinedClassName = property?.IntroDeclineDate
                    ? "intro-declined"
                    : "";

                  return (
                    <label
                      key={property?.Inv_Id}
                      className={
                        "property-radio " +
                        (isPrivateInventory ? "" : intoSentClassName + " " + intoDeclinedClassName)
                      }
                      style={isPrivateInventory ? { opacity: 0.55, cursor: 'default', pointerEvents: 'none' } : {}}
                    >
                      {!isPrivateInventory &&
                        ((property?.IsIntroSent == 1 &&
                        property?.IsCancelled == 1) ||
                        property?.IsIntroSent == 0 ||
                        !property?.IsIntroSent) &&
                        (property?.IsDecline == 0 || !property?.IsDecline) &&
                        (property?.NotInterested == 0 ||
                          !property?.NotInterested) &&
                        (property?.Not_A_Fit == 0 || !property?.Not_A_Fit) && (
                          <input
                            type="radio"
                            name="check-box-option"
                            id={property?.Inv_Id}
                            value={property?.Inv_Id}
                            checked={
                              property?.Inv_Id == selectedPropertyId
                                ? true
                                : false
                            }
                            onClick={() => handleRadioButtonChange(property)}
                            onChange={() => {}}
                          />
                        )}
                      <span>
                        <div className="left-img-wrap">
                          <img
                            className="display-thumb-img"
                            src={property.photo ? property?.photo : noImage}
                            style={isPrivateInventory ? { filter: 'grayscale(100%)' } : {}}
                          />
                        </div>
                        <div className="modal-wrap-info" style={{ flex: 1, minWidth: 0 }}>
                          <h2 style={isPrivateInventory ? { color: '#999' } : {}}>
                            {property?.Location}
                          </h2>
                          {/* Match percentage badge below address for BLS */}
                          {shouldToggleColor && !isPrivateInventory && (
                            <span style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: property?.matchScore ? 'var(--accent-f-600)' : 'var(--neutral-500, #6B7280)',
                              background: property?.matchScore ? 'var(--accent-f-050, #edf7e8)' : 'var(--neutral-100, #F3F4F6)',
                              borderRadius: 4,
                              padding: '2px 6px',
                              display: 'inline-block',
                              lineHeight: '16px',
                            }}>
                              {property?.matchScore ? `${property.matchScore}% Match` : '0% Match'}
                            </span>
                          )}
                          {isPrivateInventory ? (
                            <div className="modal-wrap-label-grey">
                              Must be listed in the MLS to send
                            </div>
                          ) : property?.IsIntroSent == 1 &&
                          property?.IsCancelled == 0 &&
                          property?.IsDecline != 1 &&
                          property?.NotInterested != 1 &&
                          property?.Not_A_Fit != 1 ? (
                            <div className="modal-wrap-label-grey">
                              SENT{" "}
                              {moment(new Date(property?.IntroSentDate)).format(
                                "MMM Do, YYYY"
                              )}
                            </div>
                          ) : property?.IsDecline == 1 ? (
                            <div className="modal-wrap-label-orange">
                              DECLINED{" "}
                              {moment(
                                new Date(property?.IntroDeclineDate)
                              ).format("MMM Do, YYYY")}
                            </div>
                          ) : property?.NotInterested == 1 ? (
                            <div className="modal-wrap-label-orange">
                              NOT INTERESTED{" "}
                              {moment(
                                new Date(property?.IntroDeclineDate)
                              ).format("MMM Do, YYYY")}
                            </div>
                          ) : property?.Not_A_Fit == 1 ? (
                            <div className="modal-wrap-label-orange">
                              NOT A FIT{" "}
                              {moment(
                                new Date(property?.IntroDeclineDate)
                              ).format("MMM Do, YYYY")}
                            </div>
                          ) : (
                            !property?.photo && (
                              <div className="modal-wrap-label-blue">
                                MUST ADD A PHOTO TO INTRODUCE
                              </div>
                            )
                          )}
                        </div>
                      </span>
                    </label>
                  );
                })}
            </div>
            {/* Hide "Add Property" for BLS agents - they manage inventory through MLS */}
            {!shouldToggleColor && (
              <div className="modal-choose-property-btnwrap">
                <button
                  className="btn btn-secondary w-100"
                  onClick={() => {
                    addNewpropertyNavigation();
                  }}
                >
                  Add Property
                </button>
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>
      <BasicPlanModal
        userDetails={userDetails}
        show={showBasicPlanModal}
        updateReloadParam={updateReloadParam}
        onHide={BasicPlanModalClose}
        fromInventoryListing={true}
        showPlanModal={BasicPlanModalShow}
      />
      <PremiumPlanModal
        userDetails={userDetails}
        show={showPremiumPlanModal}
        updateReloadParam={updateReloadParam}
        onHide={PremiumPlanModalClose}
        fromInventoryListing={true}
        showPlanModal={PremiumPlanModalShow}
      />
    </>
  );
};
export default PropertyModal;
